import { getCustomIDStatus } from "../Components/FilterBars/auditFilters";
import { isThirdPartyClaimPresent } from "../Components/Utils/claimsUtils";

/** Mid-rolls / auto-gen apply from 8 minutes; Claims Ads videoLength is in minutes. */
export const MIDROLL_MIN_DURATION_MINUTES = 8;
/** @deprecated Use MIDROLL_MIN_DURATION_MINUTES */
export const MIDROLL_MIN_DURATION_SECONDS = MIDROLL_MIN_DURATION_MINUTES;

/** Accepted average midroll spacing (minutes), with ±10% tolerance → 1.8–6.6. */
export const MIDROLL_ACCEPTABLE_MIN_MINUTES = 2;
export const MIDROLL_ACCEPTABLE_MAX_MINUTES = 6;
export const MIDROLL_ACCEPTABLE_TOLERANCE = 0.1;
export const MIDROLL_FREQUENCY_MIN_WITH_TOLERANCE =
  MIDROLL_ACCEPTABLE_MIN_MINUTES * (1 - MIDROLL_ACCEPTABLE_TOLERANCE);
export const MIDROLL_FREQUENCY_MAX_WITH_TOLERANCE =
  MIDROLL_ACCEPTABLE_MAX_MINUTES * (1 + MIDROLL_ACCEPTABLE_TOLERANCE);

const isNumericAdMetric = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const isMidrollMetricIncorrect = (value: unknown): boolean => {
  if (value === "N/A" || value === "Whitelisted" || value == null) return false;
  if (!isNumericAdMetric(value)) return false;
  return (
    value < MIDROLL_FREQUENCY_MIN_WITH_TOLERANCE ||
    value > MIDROLL_FREQUENCY_MAX_WITH_TOLERANCE
  );
};

const SHORT_CONTENT_TYPES = new Set(["SHORTS", "Short", "SHORT"]);
const ANY_HASHTAG_RE = /#\S+/;

const isUnspecifiedContentType = (contentType?: string | null): boolean =>
  !contentType ||
  contentType === "UNSPECIFIED" ||
  contentType === "Unspecified";

/** Same short detection as the scorecard metadata job. */
export const isScorecardShort = (row: {
  CreatorContentType?: string | null;
  videoTitle?: string | null;
}): boolean => {
  if (SHORT_CONTENT_TYPES.has(row.CreatorContentType ?? "")) return true;
  if (!isUnspecifiedContentType(row.CreatorContentType)) return false;
  return typeof row.videoTitle === "string" && ANY_HASHTAG_RE.test(row.videoTitle);
};

export const isMissingCustomThumbnail = (row: {
  CustomThumbnail?: string | null;
  CreatorContentType?: string | null;
  videoTitle?: string | null;
}): boolean => row.CustomThumbnail === "No" && !isScorecardShort(row);

export const customThumbnailAuditLabel = (row: {
  CustomThumbnail?: string | null;
  CreatorContentType?: string | null;
  videoTitle?: string | null;
}): string =>
  isScorecardShort(row) ? "Whitelisted" : row.CustomThumbnail ?? "";

const isBelowMidrollDuration = (videoLength: number | string | null | undefined) => {
  const length = Number(videoLength);
  return !Number.isFinite(length) || length < MIDROLL_MIN_DURATION_MINUTES;
};

// Filter options for Metadata tab
export const metadataFilterOptions = {
  customId: (row: any) => getCustomIDStatus(row) === "Incorrect",
  missingTitle: (row: any) => row.videoTitle === "Missing",
  missingDescription: (row: any) =>
    row.Description === "Missing or under 100 characters",
  incorrectCategory: (row: any) => row.categoryStatus === "Incorrect",
  incorrectMFK: (row: any) => row.mfk === "No",
  missingThumbnail: (row: any) => isMissingCustomThumbnail(row),
  // incorrectLanguage: (row: any) => row.LanguageCode === "Incorrect",
  ageRestriction: (row: any) => row.ageRestriction === "Yes",
  paidPromotion: (row: any) => row.promotion === "Yes",
  incorrectLicense: (row: any) => row.license === "Creative Common",
};

// Filter options for Claims & Ads tab
// Relies on server-processed values (N/A for Shorts / No Ads / under-8-min midrolls).
export const claimsAdsFilterOptions = {
  assetOwnership: (row: any) => row.ownership === "No",
  thirdPartyClaim: (row: any) => isThirdPartyClaimPresent(row),
  syncThirdPartyClaim: (row: any) => row.syncThirdPartyClaim === "Yes",
  unclaimedVideos: (row: any) => row.claimStatus === "No",
  missingPreRoll: (row: any) => row.preroll_count === "No",
  missingMidRolls: (row: any) =>
    !isBelowMidrollDuration(row.videoLength) && row.midroll_count === "No",
  missingPostRolls: (row: any) => row.postroll_count === "No",
  midRollFrequency: (row: any) =>
    isMidrollMetricIncorrect(row.full_midroll_timestamp),
  midRollInterval: (row: any) =>
    isMidrollMetricIncorrect(row.biggest_midroll_interval),
  autoGeneratedAds: (row: any) =>
    !isBelowMidrollDuration(row.videoLength) &&
    row.auto_generated_breaks === "Off",
};

/** Keep a claims row only when some check still fails. Empty Content ID claims are not enough. */
export const hasClaimsAdsAuditIssue = (row: any): boolean =>
  Object.values(claimsAdsFilterOptions).some((check) => check(row));
